import { cx } from './cx.js';
/**
 * RIG-407 — the one definition of what a form control looks like.
 *
 * # The bug this exists to prevent
 *
 * Eleven raw `<input>` / `<select>` / `<textarea>` elements across the Add and
 * Edit Contact dialogs, the New Task dialog and the task account picker
 * carried:
 *
 *   `block w-full rounded border-rr-border-default bg-rr-surface …`
 *
 * `border-rr-border-default` sets the border COLOUR. It does not set a border
 * width, Tailwind's preflight resets every element's border width to 0, and
 * the CRM's Tailwind config ships no `@tailwindcss/forms` plugin to restore a
 * default. So the border was zero pixels wide — and because the background
 * token was the same `bg-rr-surface` the dialog panel itself uses, each field
 * was pixel-identical to the surface behind it. A user opening "Add contact"
 * saw a dialog with labels and no visible boxes to type into.
 *
 * The Cancel button one line below was visible the whole time, because it
 * happened to include the `border` width class. The tokens were never the
 * problem; the class string was.
 *
 * # Why a shared function and not a `border` class per site
 *
 * Adding `border` to eleven className strings fixes eleven fields and leaves
 * the twelfth to be written wrong next month. `RrInput`, `RrSelect` and
 * `RrTextArea` already render correctly and now build their class list from
 * this same function, so there is ONE description of a form control and a raw
 * element composed inside a `Field`/`RrFormField` wrapper gets it too.
 *
 * # Why `onSurface`
 *
 * The fill is chosen relative to what the control sits ON. A control on the
 * page takes the surface (card-white) fill: that is why the New account form
 * reads as white boxes on the warm page background with no border at all. A
 * control INSIDE a surface-coloured panel (a dialog, a card) would be
 * white-on-white, so with `onSurface: true` it takes the page fill instead:
 * the same field-versus-container step, inverted. Both tokens are theme-aware
 * and both already existed; this picks between them, it does not add a colour.
 *
 * # Why `border-rr-border-input` (RIG-503, Option B)
 *
 * The fill step alone is subtle, and in dark mode no fill can reach 3:1 against
 * the #1F3635 card, so the border carries the boundary: `--rr-border-input` is
 * 3.42:1 against white and 3.69:1 against the dark card (pinned in
 * `tokens/form-control-contrast.test.ts`). Kenny chose to keep the surface fill
 * on page-level forms ("Create new account looks fine") and take only the
 * border, the lint and the migration from Option C; a recessed `--rr-bg-input`
 * fill was deliberately NOT adopted.
 */
export function rrControlClass(opts = {}) {
    return cx('block w-full rounded-md border text-rr-fg-primary text-sm', 
    // See "Why `onSurface`" above: page fill inside a surface panel, surface
    // fill on the page. A control the same colour as its container is invisible.
    opts.onSurface === true ? 'bg-rr-page' : 'bg-rr-surface', 
    // A textarea sizes itself by `rows`; everything else gets the control height.
    opts.multiline === true ? 'px-3 py-2' : 'h-10 px-3', 'placeholder:text-rr-fg-secondary', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rr-teal-light', 
    // RIG-503 — `border-rr-border-input` carries the boundary on its own
    // (3.42:1 light, 3.69:1 dark, both against the card), because the
    // fill/surface step is deliberately subtle and, in dark mode, no fill can
    // reach 3:1 against a #1F3635 card.
    // QA 2026-10-01 F-05: an invalid control takes the field danger color.
    // `aria-invalid` is set by RrFormField while its error shows, so every
    // control in a field turns red without each form passing `hasError`.
    opts.hasError === true ? 'border-rr-fg-danger' : 'border-rr-border-input', 'aria-[invalid=true]:border-rr-fg-danger', opts.extra);
}
//# sourceMappingURL=controlClass.js.map